import React, { useRef, useImperativeHandle, useMemo } from 'react';
import classnames from 'classnames';
import { Form, FormItemProps, FormProps, Row, Col } from 'tea-component';
import DynamicFormControl from './components/FormControl';
import { getComposedStyle } from './utils';
import type { IBaseProps } from '@home/types/common';
import type { IDynamicFormFieldValidation, IDynamicFormField } from './types';
import type { IDynamicFormControl } from './components/FormControl/types';
import './index.scss';

// DynamicForm组件实例
interface IDynamicFormInstance {
  // 获取Tea的Form组件的ref引用
  getTeaFormInstance: () => HTMLDivElement | null;
}

// DynamicForm组件的props
interface IDynamicFormProps extends IBaseProps {
  // 表单字段集合
  fields: IDynamicFormField[];
  // 表单控件映射
  controls: Record<string, IDynamicFormControl>;
  // 表单校验信息
  validations?: Record<string, IDynamicFormFieldValidation>;
  // Tea的Form组件的props
  formProps?: FormProps;
  // Tea的FormItem组件的props
  formItemProps?: FormItemProps;
  value?: Record<string, any>;
  // 整个表单change的回调
  onChange?: (value: Record<string, any>) => void;
  // 字段change的回调
  onFieldChange?: (key: string, value: any) => void;
  onFieldFocus?: (e?: any) => void;
  onFieldBlur?: (e?: any) => void;
}

const DynamicForm: React.ForwardRefRenderFunction<IDynamicFormInstance, IDynamicFormProps> = (
  props,
  ref,
) => {
  const {
    style,
    className,
    fields,
    controls,
    validations,
    formProps,
    formItemProps,
    value,
    onChange,
    onFieldChange,
    onFieldFocus,
    onFieldBlur,
    fieldsList,
    generateCustomizeAddOptionDataHandle,
    generateCustomizeDeleteOptionDataHandle,
  } = props;

  const { style: formStyle, className: formClassName } = formProps || {};
  const { style: formItemStyle, className: formItemClassName } = formItemProps || {};
  // Tea的Form组件的ref引用
  const teaForm = useRef<HTMLDivElement>(null);

  // 对外提供的方法
  useImperativeHandle(
    ref,
    () => ({
      getTeaFormInstance: () => teaForm.current,
    }),
    [],
  );

  const getCustomClass = (key: any) => {
    if (key === 'studio_entity_id') {
      return 'dbg-filter-form-item-studio-entity_id';
    }
    return '';
  };

  const renderFields = (fieldsT: any) => {
    return fieldsT?.map((field: any) => {
      const {
        style: fieldStyle,
        className: fieldClassName,
        key,
        type,
        required,
        label,
      } = field || {};
      const { status, message } = validations?.[key] || {};
      return (
        <>
          <Form.Item
            {...formItemProps}
            key={key}
            style={getComposedStyle(formItemStyle, fieldStyle)}
            className={classnames(
              'dbg-dynamic-form__form-item',
              formItemClassName,
              fieldClassName,
              getCustomClass(key),
              {
                'dbg-dynamic-form-studio-location-data': key === 'studio_location' && value?.[key],
              },
            )}
            required={required}
            label={label}
            status={status}
            message={message}
          >
            <DynamicFormControl
              type={type}
              label={label}
              control={controls?.[key]}
              value={value?.[key]}
              onChange={(payload: any) => {
                const valueTmp = { ...value, [key]: payload };
                onFieldChange?.(key, payload);
                onChange?.(valueTmp);
              }}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}
              generateCustomizeAddOptionDataHandle={generateCustomizeAddOptionDataHandle}
              generateCustomizeDeleteOptionDataHandle={generateCustomizeDeleteOptionDataHandle}
            />
          </Form.Item>
        </>
      );
    });
  };

  const renderFieldsList = (fieldsT: any, title = '') => {
    return (
      <>
        {title && (
          <Row>
            <Col span={24}>
              <div className="dbg-dynamic-form-fields-title">{title}</div>
            </Col>
          </Row>
        )}
        <div className="dbg-dynamic-form-fields-row">{renderFields(fieldsT)}</div>
      </>
    );
  };

  return (
    <Form
      {...formProps}
      ref={teaForm}
      style={getComposedStyle(style, formStyle)}
      className={classnames('gp-form dbg-dynamic-form', className, formClassName)}
    >
      {Array.isArray(fieldsList) && fieldsList?.length > 0
        ? fieldsList?.map((fieldSub: any) => {
          return renderFieldsList(fieldSub?.fields, fieldSub?.title);
        })
        : renderFields(fields)}
    </Form>
  );
};

export default React.forwardRef(DynamicForm);
